import { motion } from 'framer-motion';
import type { AudienceMeta } from '@shared/constants/audiences';
import { Container } from '../layout/Container';
import { Reveal } from '../motion/Reveal';
import { getIcon } from '../../lib/lucide-icons';
import { getAudienceTheme } from '../../config/audience-theme';
import type { AudienceProofPoint, AudienceVoice } from '../../config/audience-voice';
import { cn } from '../../lib/cn';
import { easeStripe } from '../../lib/motion';

export interface SectorProofProps {
  audience: AudienceMeta;
  voice: AudienceVoice;
  /** Override des points de preuve (par défaut on prend ceux de voice.proof.points) */
  points?: AudienceProofPoint[];
  /** Variante d'affichage : 'bento' (default, irrégulier) ou 'grid' (uniforme) */
  variant?: 'bento' | 'grid';
}

/**
 * SectorProof — Section "Pourquoi nous" / "Ce qui change pour vous".
 *
 * Variante bento moderne (asymétrique) ou grille uniforme.
 * Couleurs et icônes adaptées par audience.
 */
export function SectorProof({ audience, voice, points, variant = 'bento' }: SectorProofProps) {
  const theme = getAudienceTheme(audience.id);
  const data = points ?? voice.proof.points;

  if (data.length === 0) return null;

  return (
    <section className="bg-paper py-16 md:py-20">
      <Container>
        <Reveal>
          <div className="mx-auto mb-10 max-w-2xl text-center md:mb-12">
            <p className="eyebrow justify-center">{voice.proof.eyebrow}</p>
            <h2 className="mt-3 h-section">
              {voice.proof.title ?? 'Concrètement, ce qui change pour vous.'}
            </h2>
          </div>
        </Reveal>

        {variant === 'bento' ? (
          <BentoLayout points={data} theme={theme} />
        ) : (
          <GridLayout points={data} theme={theme} />
        )}
      </Container>
    </section>
  );
}

// ════════════════════════════════════════════════════════════════
// BENTO LAYOUT (asymétrique 2026)
// ════════════════════════════════════════════════════════════════

function BentoLayout({
  points,
  theme,
}: {
  points: AudienceProofPoint[];
  theme: ReturnType<typeof getAudienceTheme>;
}) {
  // On définit la grille en fonction du nombre de points (3 ou 4 supportés visuellement)
  const layoutClass = points.length >= 4
    ? 'grid grid-cols-1 gap-3 md:grid-cols-12 md:grid-rows-2 md:gap-4'
    : 'grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-4';

  return (
    <div className={cn('mx-auto max-w-6xl', layoutClass)}>
      {points.slice(0, 4).map((p, idx) => {
        const Icon = getIcon(p.iconName);
        // Bento : 4 cartes → 2 grandes, 2 standards
        const bentoClass =
          points.length >= 4
            ? idx === 0
              ? 'md:col-span-7 md:row-span-1'
              : idx === 1
                ? 'md:col-span-5 md:row-span-2'
                : idx === 2
                  ? 'md:col-span-4 md:row-span-1'
                  : 'md:col-span-3 md:row-span-1'
            : '';

        return (
          <motion.article
            key={p.label}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.45, delay: idx * 0.05, ease: easeStripe }}
            className={cn(
              'group relative flex flex-col gap-3 overflow-hidden rounded-2xl border border-stone-200/70 bg-paper p-5 shadow-[0_1px_2px_rgba(10,10,10,.04)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-elevated md:p-6',
              bentoClass,
            )}
          >
            {/* Glow halo dans la card au hover */}
            <span
              aria-hidden
              className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-br from-transparent via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              style={{
                background: `radial-gradient(circle at 80% 0%, currentColor 0%, transparent 70%)`,
                color: 'rgba(16,185,129,0.05)',
              }}
            />

            <div className="flex items-start justify-between gap-3">
              <div
                className={cn(
                  'inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 ease-out group-hover:scale-105',
                  theme.chipBg,
                  theme.chipIcon,
                )}
              >
                <Icon className="h-4 w-4" strokeWidth={1.7} />
              </div>
              {/* Petit numéro discret */}
              <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-stone-300">
                0{idx + 1}
              </span>
            </div>

            <div className="relative">
              <h3 className="font-display text-[15px] font-semibold leading-tight text-ink md:text-base">
                {p.label}
              </h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-stone-600">{p.sub}</p>
            </div>
          </motion.article>
        );
      })}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// GRID LAYOUT (uniforme — alternative classique)
// ════════════════════════════════════════════════════════════════

function GridLayout({
  points,
  theme,
}: {
  points: AudienceProofPoint[];
  theme: ReturnType<typeof getAudienceTheme>;
}) {
  return (
    <div className="mx-auto grid max-w-6xl gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
      {points.map((p, idx) => {
        const Icon = getIcon(p.iconName);
        return (
          <motion.article
            key={p.label}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.45, delay: idx * 0.05, ease: easeStripe }}
            className="group rounded-xl border border-stone-200/70 bg-paper p-4 transition-all duration-150 ease-out hover:-translate-y-0.5 hover:shadow-elevated"
          >
            <div
              className={cn(
                'inline-flex h-9 w-9 items-center justify-center rounded-lg',
                theme.chipBg,
                theme.chipIcon,
              )}
            >
              <Icon className="h-4 w-4" strokeWidth={1.7} />
            </div>
            <h3 className="mt-3 font-display text-[14px] font-semibold leading-tight text-ink">
              {p.label}
            </h3>
            <p className="mt-1 text-xs text-stone-600 leading-relaxed">{p.sub}</p>
          </motion.article>
        );
      })}
    </div>
  );
}
